import { twMerge } from 'tailwind-merge';

import { getDefaultImageProperties, getTestProps } from '../../lib/helpers';

import {
  SpinnerGridIcon,
  SpinnerIcon,
  SpinnerWhiteIcon,
} from '../../images/shapes';

const animateClass = {
  none: '',
  spin: 'animate-spin mx-autoanimate-[pulse_1.5s_ease-in-out_infinite]',
  pulse: 'animate-pulse mx-autoanimate-[pulse_1.5s_ease-in-out_infinite]',
};

const sizeClass = {
  tiny: 'h-6 w-6',
  small: 'h-8 w-8',
  normal: 'h-12 w-12',
  big: 'h-14 w-14',
  veryBig: 'h-16 w-16',
};

export type LoaderType = 'image' | 'spinner' | 'spinner-white' | 'spinner-grid';

interface LoaderProps {
  /**
   * Testing prop type
   */
  testId?: string;
  /**
   * Loader src
   */
  src?: string;
  /**
   * Loader type image or spinner
   */
  type?: LoaderType;
  /**
   * Loader animation loaders
   */
  animate?: keyof typeof animateClass;
  /**
   * Loader image alt
   */
  alt?: string;
  /**
   * Loader size
   */
  size?: keyof typeof sizeClass;
  /**
   * Loader additional CSS classes
   */
  additionalClasses?: string;
}

const Loader = ({
  testId = 'loader',
  src,
  size = 'normal',
  animate = 'spin',
  type = 'spinner',
  additionalClasses = '',
}: LoaderProps) => {
  const srcType = {
    image: src ? (
      <img
        src={src}
        alt=""
        className="w-full h-full"
        {...getDefaultImageProperties()}
        {...getTestProps(testId, 'image')}
      />
    ) : (
      <SpinnerIcon
        className="w-full h-full"
        {...getTestProps(testId, 'spinner')}
      />
    ),
    spinner: (
      <SpinnerIcon
        className="w-full h-full dark:text-slate-200"
        {...getTestProps(testId, 'spinner')}
      />
    ),
    'spinner-white': (
      <SpinnerWhiteIcon
        className="w-full h-full"
        {...getTestProps(testId, 'spinner-white')}
      />
    ),
    'spinner-grid': (
      <SpinnerGridIcon
        className="w-full h-full"
        {...getTestProps(testId, 'spinner-grid')}
      />
    ),
  };

  return (
    <div
      className={twMerge(
        'mx-autoanimate-[pulse_1.5s_ease-in-out_infinite]',
        sizeClass[size] || sizeClass.normal,
        animateClass[animate] || animateClass.none,
        additionalClasses,
      )}
      data-loader
      {...getTestProps(testId)}
    >
      {srcType[type] || srcType.spinner}
    </div>
  );
};

export default Loader;
